/** Danger zone (screens §F1): abort a stuck run, or roll back the last resolved or released round. Nothing is deleted. */
import { useState } from "react";
import type { RoundSummary } from "@bsim/contracts";
import { Dialog, DialogCancel } from "../theme/Dialog";
import type { Act } from "./useAct";

const MIN_REASON = 10;

export function DangerZone({ cohortId, rounds, band, act, onClose }: {
  readonly cohortId: string; readonly rounds: readonly RoundSummary[]; readonly band: RoundSummary; readonly act: Act; readonly onClose: () => void;
}) {
  const stuck = band.state === "resolving" || band.state === "resolvingPhase2";
  const rollbackRound = [...rounds].reverse().find((r) => r.state === "resolved" || r.state === "released");
  const [abortReason, setAbortReason] = useState("");
  const [target, setTarget] = useState<"locked" | "open">("locked");
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");

  const abort = async () => { if ((await act("roundAbortResolve", { cohortId, roundId: band.id }, { expectedStateVersion: band.stateVersion, reason: abortReason })).ok) onClose(); };
  const rollback = async () => {
    if (rollbackRound === undefined) return;
    const res = await act("roundRollback", { cohortId, roundId: rollbackRound.id }, { expectedStateVersion: rollbackRound.stateVersion, target, reason });
    if (res.ok) onClose();
  };

  return (
    <Dialog title="Danger zone" testId="dialog.danger" onClose={onClose} lead="Rollback supersedes results; nothing is deleted." footer={<><span className="gx-grow" /><DialogCancel onClose={onClose} label="Close" /></>}>
      <section className="gx-fac-dr gx-fac-dr--stack">
        <b>Abort a stuck run</b>
        <span className="gx-small gx-muted">{stuck ? "Only once the run is older than three times the usual time." : "Available while the year is running."}</span>
        <input className="gx-fac-input" aria-label="Reason for aborting" placeholder="Reason (at least 10 characters)" value={abortReason} disabled={!stuck} onChange={(e) => setAbortReason(e.target.value)} />
        <button type="button" className="gx-btn gx-btn--danger" disabled={!stuck || abortReason.trim().length < MIN_REASON} data-testid="danger.abort" onClick={() => void abort()}>Abort the run</button>
      </section>
      <section className="gx-fac-dr gx-fac-dr--stack">
        <b>Roll back {rollbackRound === undefined ? "a round" : `Round ${rollbackRound.roundNo}`}</b>
        <span className="gx-small gx-muted">Last resolved or released round only.</span>
        <select className="gx-fac-input gx-select" aria-label="Roll back to" value={target} disabled={rollbackRound === undefined} onChange={(e) => setTarget(e.target.value === "open" ? "open" : "locked")}>
          <option value="locked">Back to closed</option>
          <option value="open">Back to open</option>
        </select>
        <input className="gx-fac-input" aria-label="Reason for rolling back" placeholder="Reason (at least 10 characters)" value={reason} disabled={rollbackRound === undefined} onChange={(e) => setReason(e.target.value)} />
        <input className="gx-fac-input" aria-label="Type the round number to confirm" placeholder="Type the round number to confirm" value={typed} disabled={rollbackRound === undefined} onChange={(e) => setTyped(e.target.value)} />
        <button type="button" className="gx-btn gx-btn--danger" disabled={rollbackRound === undefined || reason.trim().length < MIN_REASON || typed.trim() !== String(rollbackRound?.roundNo)} data-testid="danger.rollback" onClick={() => void rollback()}>Roll back</button>
      </section>
    </Dialog>
  );
}
